import React, {
  useCallback, useEffect, useMemo, useState, useRef, Fragment, RefObject, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Grid from '../../../components/grid/grid.component';
import GridItem from '../../../components/grid-item/grid-item.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import Loader from '../../../components/loader/loader.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import ActionButton from '../../../components/action-button/action-button.component';
import Checkbox from '../../../components/checkbox/checkbox.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Hint from '../../../components/hint/hint.component';
import TextBlock from '../../../components/text-block/text-block.component';
import LineBreak from '../../../components/line-break/line-break.component';
import IconOffsetBlock from '../../../components/icon-offset-block/icon-offset-block.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldPassword from '../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import CommentTable from '../../app/common/comment-table/comment-table.component';
import CommentForm from '../../app/common/comment-form/comment-form.component';
import ControlButtonBlock from '../../app/common/control-button-block/control-button-block.component';
import DeleteIcon from '../../../icons/delete-icon';
import DownloadIcon from '../../../icons/download-icon';
import AttachmentIcon from '../../../icons/attachment-icon';
import FormService from '../../../services/form-service';
import FormatService from '../../../services/format-service';
import CommentService from '../../../services/comment-service';
import StructureService from '../../../services/structure-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import {
  CONTACT_VIEW, DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW, DEAL_PARTICIPANT_COMMENT_LIST_VIEW, DEAL_PARTICIPANT_EDIT,
  SETTINGS_USER_CREATE, DEAL_PARTICIPANT_COMMENT_CREATE, DEAL_PARTICIPANT_COMMENT_EDIT,
  DEAL_PARTICIPANT_COMMENT_DELETE, DEAL_PARTICIPANT_APPROVAL_VIEW, SEARCH_RESULT_VIEW,
} from '../../../constants/entity/permissions';
import {
  KEYBOARD_KEY_ARROW_UP, KEYBOARD_KEY_ENTER, KEYBOARD_KEY_ESCAPE,
} from '../../../constants/system/keyboard-keys';
import { ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED } from '../../../constants/entity/account-user-type-lists';
import { TIMEOUT_DEFAULT } from '../../../constants/system/timeouts';
import { DATA_CONTACT_ID } from '../../../constants/system/data-attributes';
import { SIGN_HASH, SIGN_AT } from '../../../constants/system/signs';
import { ROUTE_CONTACT, ROUTE_SEARCH } from '../../../constants/system/routes';
import { HTML_TAG_NAME_ANCHOR } from '../../../constants/system/html-tag-names';
import ACCOUNT_USER_TYPE_GROUP_EXTERNAL from '../../../constants/entity/account-user-type-groups';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const DealParticipantModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MODAL_TITLE_DEFAULT: string = 'New Participant';
  const MODAL_TITLE_EDIT_PREFIX: string = 'Edit';
  const ID_PREFIX_CHECKBOX_EXPORT_MODAL: string = 'checkbox-export-modal';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isCommentSaving,
    isEditMode,
    isNewDeal,
    isModalEditMode,
    isRightDrawerOpen,
    isCommentFormActive,
    isNewEntity,
    dealId,
    ndaFile,
    currentUser,
    dealParticipantData,
    queryCommentId,
    accountUserTypeList,
    dealParticipantGroupList,
    accessLevelList,
    contactPersonList,
    contactNotParticipantList,
    contactLinkedUserList,
    roleList,
    tagList,
    saveFormDeps,
    onCreate,
    onCancel,
    onCloseModal,
    onCancelForm,
    onSubmitForm,
    onSubmitCreateForm,
    onContactClick,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onFileViewClick,
    onNdaFileChange,
    onNdaFileDelete,
    onContactChange,
    onSwitchModalEditMode,
    onSwitchNewUserFormActive,
    onNewUserSendInvitationCheckboxChange,
    onAccountUserTypeGroupChange,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactNotParticipantSearch,
    onContactNotParticipantSearchClose,
    onCommentAttachmentFileViewClick,
    onCommentEntityLinkClick,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const permissionList: string[] = useCurrentUserPermissions();
  const contentContainerRef: RefObject<HTMLDivElement> | null = useRef(null);

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [accountUserTypeGroup, setAccountUserTypeGroup]: [string | null, Function] = useState(null);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [isContactLinkedToUser, setIsContactLinkedToUser]: [boolean, Function] = useState(true);
  const [isNewUserFormActive, setIsNewUserFormActive]: [boolean, Function] = useState(false);
  const [isUserInvitationView, setIsUserInvitationView]: [boolean, Function] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
  const [isPasswordConfirmVisible, setIsPasswordConfirmVisible]: [boolean, Function] = useState(false);
  const [commentActiveId, setCommentActiveId]: [number | null, Function] = useState(null);
  const [contentHeight, setContentHeight]: [number | null, Function] = useState(null);
  const [commentEditId, setCommentEditId]: [number | null, Function] = useState(null);
  const [commentText, setCommentText]: [string | null, Function] = useState(null);
  const [commentAttachmentFiles, setCommentAttachmentFiles]: [File[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentAttachments, setCommentAttachments]: [AttachmentInput[] | null, Function]
    = useState<StateValueDefault>(null);

  const dealParticipantApprovalViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_APPROVAL_VIEW,
  ], []);
  const dealParticipantAccessLevelViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
  ], []);
  const dealParticipantCommentListViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_COMMENT_LIST_VIEW,
  ], []);
  const dealParticipantCommentCreatePermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_COMMENT_CREATE,
  ], []);
  const dealParticipantCommentEditPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_COMMENT_EDIT,
  ], []);
  const dealParticipantCommentDeletePermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_COMMENT_DELETE,
  ], []);
  const settingsUserCreatePermissions: string[] = useMemo((): string[] => [
    SETTINGS_USER_CREATE,
  ], []);
  const isAccessLevelView: boolean = !!accountUserTypeGroup
    && accountUserTypeGroup === ACCOUNT_USER_TYPE_GROUP_EXTERNAL;
  const isDealParticipantCommentListVisible: boolean = StructureService.checkPermission(
    DEAL_PARTICIPANT_COMMENT_LIST_VIEW, permissionList,
  );
  const isFullWidthTable: boolean = isSmallScreen || isEditMode || isNewEntity || isNewDeal
    || isModalEditMode || !isDealParticipantCommentListVisible;
  const commentList: Comment[] = (dealParticipantData && dealParticipantData.comments) || [];
  const attachmentList: Attachment[] = (dealParticipantData && dealParticipantData.attachments) || [];
  const isApprovalViewEnabled: boolean = StructureService.checkPermission(
    DEAL_PARTICIPANT_APPROVAL_VIEW, permissionList,
  );

  const commentAttachmentDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    commentAttachmentFiles,
  ], [commentAttachmentFiles]);
  const commonAttachmentFiles: (AttachmentInput | File)[] = useMemo((): (AttachmentInput | File)[] => [
    ...(commentAttachments || []),
    ...(commentAttachmentFiles || []),
  ], [commentAttachments, commentAttachmentFiles]);

  const filteredAccountUserTypeList: AccountUserType[] = useMemo((): AccountUserType[] => (
    accountUserTypeList && accountUserTypeList.filter((accountUserTypeItem: AccountUserType): boolean => (
      ACCOUNT_USER_TYPE_LIST_DEAL_LIMITED.includes(accountUserTypeItem.name)
    ))
  ), [accountUserTypeList]);

  const modalTitle: string = useMemo((): string => {
    if (!isNewEntity && dealParticipantData && dealParticipantData.contact
      && dealParticipantData.contact.fullName) {
      return `${(isEditMode || isModalEditMode)
        ? `${MODAL_TITLE_EDIT_PREFIX} ` : ''}${dealParticipantData.contact.fullName}`;
    }

    return MODAL_TITLE_DEFAULT;
  }, [isNewEntity, isEditMode, isModalEditMode, dealParticipantData]);

  const dealParticipantId: number = (!isNewEntity && dealParticipantData && dealParticipantData.id)
    || FormService.FORM_KEY_NEW;
  const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);
  const formPrefix: string = `dealData.dealParticipantsData[${formKeyDealParticipant}]`;
  const withEditModeSwitcher: boolean = !isNewEntity && !isNewDeal && !isEditMode;

  const mentionList: MentionItem[] = useMemo((): MentionItem[] => (
    contactLinkedUserList && contactLinkedUserList.length
      ? contactLinkedUserList.map((contactItem: Contact): MentionItem => ({
        id: `${SIGN_AT}${contactItem.fullName}`,
        contactId: contactItem.id,
        fullName: contactItem.fullName,
        photo: contactItem.photo,
        photoThumbnail: contactItem.photoThumbnail,
      })) : []
  ), [contactLinkedUserList]);

  const hashtagList: HashtagItem[] = useMemo((): HashtagItem[] => (
    tagList && tagList.length ? tagList.map((tagItem: Tag): HashtagItem => ({
      id: `${SIGN_HASH}${tagItem.name}`,
      tagId: tagItem.id,
      name: tagItem.name,
    })) : []
  ), [tagList]);

  const menuItemDealParticipantGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: dealParticipantGroupList,
  }), [dealParticipantGroupList]);

  const menuItemContactPersonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
  }), [contactPersonList]);

  const menuItemContactNotParticipantList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: contactNotParticipantList,
    defaultImage: PersonDefaultImage,
  }), [contactNotParticipantList]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: isContactLinkedToUser || isNewUserFormActive ? accessLevelList : [],
  }), [accessLevelList, isContactLinkedToUser, isNewUserFormActive]);

  const menuItemRoleList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: roleList,
  }), [roleList]);

  const menuItemAccountUserTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: true,
    groupKey: 'group',
    groupItemLabelKey: 'title',
    optionItemValueKey: 'name',
    optionList: filteredAccountUserTypeList,
  }), [filteredAccountUserTypeList]);

  const resetForm: Function = useCallback((): void => {
    setIsContactLinkedToUser(true);
    setIsNewUserFormActive(false);
    setIsUserInvitationView(false);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setIsMessageError(false);
    setStatusMessage('');
  }, []);

  const onSaveCommentSuccess: Function = useCallback((commentId?: number): void => {
    const lastComment: Comment | null = commentList && commentList.length ? commentList[commentList.length - 1] : null;
    const resultCommentId: number | null = commentId || (lastComment && lastComment.id) || null;

    if (resultCommentId) {
      CommentService.scrollToComment(resultCommentId);
    }
  }, [commentList]);

  const saveComment: Function = useCallback((): void => {
    const mentionContactIds: number[] = commentText
      ? FormatService.extractHtmlAttributes(commentText, HTML_TAG_NAME_ANCHOR, DATA_CONTACT_ID, true) as number[] : [];

    if (commentEditId) {
      if (onUpdateComment && (commentText || (commentAttachments && commentAttachments.length)
        || (commentAttachmentFiles && commentAttachmentFiles.length))) {
        onUpdateComment(
          dealParticipantId, commentText, commentAttachments, commentAttachmentFiles,
          commentEditId, mentionContactIds, onSaveCommentSuccess,
        );
      }

      setCommentEditId(null);
    } else if (onAddComment && (commentText || (commentAttachmentFiles && commentAttachmentFiles.length))) {
      onAddComment(dealParticipantId, commentText, commentAttachmentFiles, mentionContactIds, onSaveCommentSuccess);
    }

    setCommentText(null);
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
  }, [commentText, commentAttachmentFiles, commentAttachments, commentEditId, dealParticipantId, commentList]);

  const onCommentFieldChange: Function = useCallback((event: React.ChangeEvent, commentTextValue: string): void => {
    setCommentText(commentTextValue);
  }, []);

  const onCommentFieldKeyDown: Function = useCallback((event: React.KeyboardEvent, commentTextValue: string): void => {
    if (event.key === KEYBOARD_KEY_ARROW_UP && !commentTextValue) {
      CommentService.editLastComment();

      event.preventDefault();
    }

    if (event.key === KEYBOARD_KEY_ESCAPE) {
      onCancelCommentForm();
    }

    if (event.key === KEYBOARD_KEY_ENTER && !event.shiftKey) {
      setCommentText(commentTextValue);

      CommentService.sendComment();
    }
  }, []);

  const onCancelCommentForm: Function = useCallback((): void => {
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
  }, []);

  const onDeleteCommentConfirm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (onDeleteComment) {
      onDeleteComment(dealParticipantId, commentId, onSaveCommentSuccess);
    }

    onCancelCommentForm();

    event.stopPropagation();
  }, [dealParticipantId, commentList]);

  const onDeleteCommentCancel: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (commentActiveId === commentId) {
      setCommentActiveId(null);
    }

    event.stopPropagation();
  }, [commentActiveId]);

  const onCommentActiveIdChange: Function = useCallback((
    event: React.ChangeEvent, commentId: number,
  ): void => {
    setCommentActiveId(commentId);

    event.stopPropagation();
  }, []);

  const switchCommentEditMode: Function = useCallback((event: React.SyntheticEvent, comment: Comment): void => {
    const isEditModeActive: boolean = !commentEditId || commentEditId !== comment.id;

    setCommentEditId(isEditModeActive ? comment.id : null);
    setCommentText(isEditModeActive ? (comment.text || '') : null);

    const commentAttachmentItems: Attachment[] | null = comment.attachments && comment.attachments.length
      ? comment.attachments : null;
    setCommentAttachments(isEditModeActive && commentAttachmentItems
      ? commentAttachmentItems.map((attachmentItem: Attachment): AttachmentInput => ({
        id: attachmentItem.id,
        name: attachmentItem.file.name,
        link: attachmentItem.file.linkName,
      })) : null);
    setCommentAttachmentFiles(isEditModeActive ? [] : null);

    event.stopPropagation();
  }, [commentEditId, commentList]);

  const onCommentAttachmentFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, fileIndex: number,
  ): void => {
    const isCommentEditing: boolean = !!commentEditId;
    const currentCommentAttachmentFiles: File[] = [...(commentAttachmentFiles || [])];
    const currentCommentAttachments: AttachmentInput[] = [...(commentAttachments || [])];

    if (fileIndex !== null && fileIndex >= 0) {
      if (isCommentEditing && fileIndex < currentCommentAttachments.length) {
        const newCommentAttachments: AttachmentInput[] = currentCommentAttachments;
        StructureService.deleteListItemByIndex(newCommentAttachments, fileIndex);
        setCommentAttachments(newCommentAttachments);
      } else {
        const newCommentAttachmentFiles: File[] = currentCommentAttachmentFiles;
        StructureService.deleteListItemByIndex(
          newCommentAttachmentFiles,
          fileIndex - (isCommentEditing ? currentCommentAttachments.length : 0),
        );
        setCommentAttachmentFiles(newCommentAttachmentFiles);
      }
    }
  }, [commentAttachmentFiles, commentAttachments]);

  const onCommentAttachmentFilesChange: Function = useCallback((files: File[]): void => {
    setCommentAttachmentFiles([...(commentAttachmentFiles || []), ...files]);
  }, [commentAttachmentFiles]);

  const onCommentAttachmentFileError: Function = useCallback((error: string): void => {
    setStatusMessage(error);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel && !isEditMode) {
      onCancel();
    } else if (onCloseModal) {
      onCloseModal();
    }

    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
  }, [isEditMode]);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    onSubmitCreateForm((): void => {
      if ((commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) && dealParticipantId) {
        saveComment();
      }

      if (commentText || (commentAttachmentFiles && commentAttachmentFiles.length)) {
        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);
        setIsMessageError(false);
      }

      if (isNewEntity && onCreate) {
        onCreate();
        resetForm();
      } else if (onCloseModal) {
        onCloseModal();
      }

      setCommentAttachmentFiles(null);
      setCommentAttachments(null);
    });
  }, [
    isNewEntity, isEditMode, dealParticipantId, commentText, commentAttachmentFiles, ...(saveFormDeps || []),
  ]);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && (isNewEntity || isModalEditMode) && !isEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else {
      resetForm();

      setCommentAttachmentFiles(null);
      setCommentAttachments(null);

      if (isModalEditMode && onCancel) {
        onCancel();
      }

      if (onCloseModal) {
        onCloseModal();
      }
    }
  }, [isSaveDisabled, isNewEntity, isModalEditMode, isEditMode]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    resetForm();

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const contactChange: Function = useCallback((contactId: number): void => {
    const contact: Contact | null = contactPersonList && contactPersonList.length && contactId
      ? contactPersonList.find((contactItem: Contact): boolean => contactItem.id === contactId) : null;
    const isSelectedContactLinkedToUser: boolean = !!contact && !!contact.user;

    if (contact) {
      setIsContactLinkedToUser(isSelectedContactLinkedToUser);
    }

    if (onContactChange) {
      onContactChange(dealParticipantId, contact, isSelectedContactLinkedToUser);
    }
  }, [dealParticipantId, contactPersonList]);

  const accountUserTypeChange: Function = useCallback((accountUserTypeName: string): void => {
    const accountUserTypeListItem: AccountUserType = accountUserTypeList.find((
      accountUserTypeItem: AccountUserType,
    ): boolean => (
      accountUserTypeItem.name === accountUserTypeName
    ));
    const accountUserTypeGroupName: string | null = accountUserTypeListItem.group
      ? accountUserTypeListItem.group.name : null;

    setAccountUserTypeGroup(accountUserTypeGroupName);

    if (onAccountUserTypeGroupChange) {
      onAccountUserTypeGroupChange(dealParticipantId, accountUserTypeGroupName);
    }
  }, [dealParticipantId, accountUserTypeList]);

  const switchNewUserFormActive: CheckboxChangeHandlerFunction = useCallback((
    event?: React.ChangeEvent,
    isNewUserFormActiveValue?: boolean,
  ): void => {
    setIsNewUserFormActive(isNewUserFormActiveValue);

    if (onSwitchNewUserFormActive) {
      onSwitchNewUserFormActive(dealParticipantId, isNewUserFormActiveValue);
    }
  }, [dealParticipantId]);

  const newUserSendInvitationCheckboxChange: CheckboxChangeHandlerFunction = useCallback((
    event?: React.ChangeEvent<HTMLInputElement>,
    isUserInvitationViewValue?: boolean,
  ): void => {
    setIsUserInvitationView(!!isUserInvitationViewValue);

    if (onNewUserSendInvitationCheckboxChange && isUserInvitationViewValue) {
      onNewUserSendInvitationCheckboxChange(dealParticipantId);
    }
  }, [dealParticipantId]);

  const onPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordVisible(value);
  }, []);

  const onPasswordConfirmVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordConfirmVisible(value);
  }, []);

  const ndaFileChange: Function = useCallback((file: File): void => {
    if (onNdaFileChange) {
      onNdaFileChange(dealParticipantId, file);
    }
  }, [dealParticipantId]);

  const ndaFileDelete: MouseEventHandlerFunction = useCallback((): void => {
    if (onNdaFileDelete) {
      onNdaFileDelete(dealParticipantId);
    }
  }, [dealParticipantId]);

  useEffect((): void => {
    if (isNewEntity || (dealParticipantData && (!dealParticipantData.contact
      || !dealParticipantData.contact.user))) {
      setIsContactLinkedToUser(false);
    }
  }, [dealParticipantData]);

  useEffect((): ReactUseEffectCleanUp => {
    const timeoutId: TimeoutId = setTimeout((): void => {
      if (contentContainerRef && contentContainerRef.current) {
        const contentContainer: ChildNode | null = (contentContainerRef.current as HTMLElement).firstChild;

        if (contentContainer) {
          setContentHeight((contentContainer as HTMLElement).clientHeight);
        }
      }
    }, TIMEOUT_DEFAULT);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [contentContainerRef, isOpen, attachmentList]);

  return (
    <Fragment>
      {
        (isNewEntity || !!dealParticipantData) && (
          <ModalLayout
            isOpen={isOpen}
            isMedium={!isFullWidthTable}
            isModalEditMode={isModalEditMode}
            withEditModeSwitcher={withEditModeSwitcher}
            title={modalTitle}
            editPermission={DEAL_PARTICIPANT_EDIT}
            onCloseModal={cancelChangesAndCloseModal}
            onCloseModalByClickAway={closeModalByClickAway}
            onSwitchModalEditMode={onSwitchModalEditMode}
          >
            <Form>
              <Grid withSmallSpacing>
                <GridItem isFullWidth={isFullWidthTable} isMediumWidth={!isFullWidthTable}>
                  <Card isContainer isFullHeight withNoPadding ref={contentContainerRef}>
                    <Table>
                      <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && (
                            <TableRow>
                              <TableCell>
                                Contact
                                {
                                  (isEditMode || isNewDeal || isNewEntity || isModalEditMode) && (
                                    <RequiredMark />
                                  )
                                }
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  withSearch
                                  withBlankElement={isNewEntity}
                                  isDisabled={isNewEntity ? contactNotParticipantList
                                    && !contactNotParticipantList.length
                                    : contactPersonList && !contactPersonList.length}
                                  searchOptions={isNewEntity ? {
                                    dealId: dealId || null,
                                  } : undefined}
                                  name={`${formPrefix}.contactId`}
                                  optionsTotal={isNewEntity ? contactNotParticipantList
                                    && contactNotParticipantList.length : contactPersonList && contactPersonList.length}
                                  onSearch={isNewEntity ? onContactNotParticipantSearch
                                    : onContactPersonSearch}
                                  onClose={isNewEntity ? onContactNotParticipantSearchClose
                                    : onContactPersonSearchClose}
                                  onChange={(contactId: number): void => {
                                    contactChange(contactId);
                                  }}
                                >
                                  {isNewEntity ? menuItemContactNotParticipantList : menuItemContactPersonList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode
                          && dealParticipantData.contact && dealParticipantData.contact.company && (
                            <TableRow>
                              <TableCell>
                                Company
                              </TableCell>
                              <TableCell>
                                <ChipLink
                                  isDisabled={isEditMode}
                                  label={dealParticipantData.contact.company.name}
                                  imageLink={dealParticipantData.contact.company.photoThumbnail
                                    || dealParticipantData.contact.company.photo || CompanyDefaultImage}
                                  permission={CONTACT_VIEW}
                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                    onContactClick(event, dealParticipantData.contact.company.id);
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode
                          && dealParticipantData.contact && dealParticipantData.contact.email && (
                            <TableRow>
                              <TableCell>
                                Email
                              </TableCell>
                              <TableCell>
                                <FilteredLink
                                  isEmail
                                  link={dealParticipantData.contact.email}
                                >
                                  {dealParticipantData.contact.email}
                                </FilteredLink>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode
                          && dealParticipantData.contact && dealParticipantData.contact.phone
                          && dealParticipantData.contact.phoneText && (
                            <TableRow>
                              <TableCell>
                                Phone
                              </TableCell>
                              <TableCell>
                                <FilteredLink
                                  isPhone
                                  link={FormatService.formatCleanIntegerNumberString(dealParticipantData.contact.phone)}
                                  phoneExtension={dealParticipantData.contact.phoneExtension}
                                >
                                  {
                                    `${dealParticipantData.contact.phoneText}`
                                    + `${dealParticipantData.contact.phoneExtension
                                      ? ` ext. ${dealParticipantData.contact.phoneExtension}` : ''}`
                                  }
                                </FilteredLink>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && !isContactLinkedToUser && (
                            <CheckAccess permissions={settingsUserCreatePermissions}>
                              <TableRow>
                                <TableCell>
                                  User
                                </TableCell>
                                <TableCell>
                                  This contact is not linked to user, access level cannot be applied.
                                  <LineBreak />
                                  Link it to new user
                                  <Checkbox
                                    withOffset
                                    id={`${ID_PREFIX_CHECKBOX_EXPORT_MODAL}-${dealParticipantId}`}
                                    onChange={switchNewUserFormActive}
                                  />
                                </TableCell>
                              </TableRow>
                            </CheckAccess>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && (
                            <TableRow>
                              <TableCell>
                                Email
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldText isBordered withNoAutocomplete name={`${formPrefix}.user.email`} />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && (
                            <TableRow>
                              <TableCell>
                                Role
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  isDisabled={roleList && !roleList.length}
                                  name={`${formPrefix}.user.role`}
                                  optionsTotal={roleList && roleList.length}
                                >
                                  {menuItemRoleList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && (
                            <TableRow>
                              <TableCell>
                                Type
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldSelect
                                  isBordered
                                  isDisabled={filteredAccountUserTypeList && !filteredAccountUserTypeList.length}
                                  name={`${formPrefix}.user.accountUserType`}
                                  optionsTotal={filteredAccountUserTypeList && filteredAccountUserTypeList.length}
                                  onChange={((accountUserTypeName: string): void => {
                                    accountUserTypeChange(accountUserTypeName);
                                  })}
                                >
                                  {menuItemAccountUserTypeList}
                                </FormFieldSelect>
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && (
                            <TableRow>
                              <TableCell>
                                Send Invitation
                                <Hint
                                  title={'By choosing this option an inactive user will be created '
                                    + 'with a message sent to specified email. User needs to follow the link from '
                                    + 'email and fill out register form.'}
                                />
                              </TableCell>
                              <TableCell>
                                <FormFieldCheckbox
                                  name={`${formPrefix}.user.withSendInvitation`}
                                  onChange={newUserSendInvitationCheckboxChange}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && isUserInvitationView && (
                            <TableRow>
                              <TableCell>
                                Additional Invitation Message
                              </TableCell>
                              <TableCell>
                                <FormFieldText
                                  isBordered
                                  isMultiline
                                  name={`${formPrefix}.user.invitationMessage`}
                                  minRows={FormService.TEXTAREA_MIN_ROWS_SMALL}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && !isUserInvitationView && (
                            <TableRow>
                              <TableCell>
                                Password
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldPassword
                                  isBordered
                                  withStrengthBar
                                  withNoAutocomplete
                                  isVisible={isPasswordVisible}
                                  name={`${formPrefix}.user.password`}
                                  onVisibilityChange={onPasswordVisibilityChange}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          isNewEntity && isNewUserFormActive && !isUserInvitationView && (
                            <TableRow>
                              <TableCell>
                                Confirm Password
                                <RequiredMark />
                              </TableCell>
                              <TableCell>
                                <FormFieldPassword
                                  isBordered
                                  withNoAutocomplete
                                  isVisible={isPasswordConfirmVisible}
                                  name={`${formPrefix}.user.passwordConfirm`}
                                  onVisibilityChange={(value: boolean): void => {
                                    onPasswordConfirmVisibilityChange(value);
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        }
                        <TableRow>
                          <TableCell>
                            Group
                            {
                              (isEditMode || isNewDeal || isNewEntity || isModalEditMode) && (
                                <RequiredMark />
                              )
                            }
                          </TableCell>
                          <TableCell>
                            {
                              isEditMode || isNewEntity || isNewDeal || isModalEditMode ? (
                                <FormFieldSelect
                                  isBordered
                                  withBlankElement
                                  isDisabled={dealParticipantGroupList && !dealParticipantGroupList.length}
                                  name={`${formPrefix}.groupId`}
                                  optionsTotal={dealParticipantGroupList && dealParticipantGroupList.length}
                                >
                                  {menuItemDealParticipantGroupList}
                                </FormFieldSelect>
                              ) : dealParticipantData.group.name
                            }
                          </TableCell>
                        </TableRow>
                        {
                          (
                            ((isEditMode || isNewDeal || isNewEntity || isModalEditMode)
                              && isApprovalViewEnabled)
                            || (dealParticipantData && dealParticipantData.isApproved && !isEditMode && !isNewDeal
                              && !isNewEntity && !isModalEditMode)
                          ) && (
                            <TableRow>
                              <TableCell>
                                Approved
                              </TableCell>
                              <TableCell>
                                {
                                  ((isEditMode || isNewEntity || isNewDeal || isModalEditMode)
                                    && isApprovalViewEnabled) ? (
                                    <FormFieldCheckbox name={`${formPrefix}.isApproved`} />
                                  ) : FormatService.formatBooleanValue(dealParticipantData.isApproved)
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (
                            (isEditMode || isNewDeal || isNewEntity || isModalEditMode)
                            || (dealParticipantData && dealParticipantData.ndaFile)
                          ) && (
                            <CheckAccess permissions={dealParticipantApprovalViewPermissions}>
                              <TableRow>
                                <TableCell>
                                  NDA File
                                </TableCell>
                                <TableCell>
                                  {
                                    (isEditMode || isNewDeal || isNewEntity || isModalEditMode) ? (
                                      <FlexContainer>
                                        {
                                          !ndaFile && ndaFile !== null
                                          && (dealParticipantData && dealParticipantData.ndaFile) && (
                                            <FlexContainer>
                                              <PaddedFlexContainer>
                                                <AttachmentIcon />
                                              </PaddedFlexContainer>
                                              <TextBlock>
                                                {dealParticipantData.ndaFile.name}
                                              </TextBlock>
                                              <ActionButton
                                                isActive
                                                isInline
                                                withNoHighlight
                                                withSmallPadding
                                                onClick={ndaFileDelete}
                                              >
                                                <DeleteIcon />
                                              </ActionButton>
                                            </FlexContainer>
                                          )
                                        }
                                        <FormFieldUpload
                                          isDroppable
                                          uploadFile={ndaFile}
                                          name={`${formPrefix}.ndaFile`}
                                          buttonLabel="Upload New File"
                                          onFileChange={ndaFileChange}
                                          onFileDelete={ndaFileDelete}
                                        />
                                      </FlexContainer>
                                    ) : (
                                      <FlexContainer>
                                        <FlexContainer>
                                          {dealParticipantData.ndaFile.name}
                                        </FlexContainer>
                                        <PaddedFlexContainer>
                                          <FilteredLink
                                            isExternal
                                            link={dealParticipantData.ndaFile.link}
                                            onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                              onFileViewClick(
                                                event,
                                                dealParticipantData.ndaFile,
                                                dealParticipantData.name,
                                              );
                                            }}
                                          >
                                            <IconOffsetBlock offsetTop={1}>
                                              <DownloadIcon />
                                            </IconOffsetBlock>
                                          </FilteredLink>
                                        </PaddedFlexContainer>
                                      </FlexContainer>
                                    )
                                  }
                                </TableCell>
                              </TableRow>
                            </CheckAccess>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal
                            || isModalEditMode || dealParticipantData.name) && (
                            <TableRow>
                              <TableCell>
                                Title
                              </TableCell>
                              <TableCell>
                                {
                                  isEditMode || isNewEntity || isNewDeal || isModalEditMode ? (
                                    <FormFieldText isBordered name={`${formPrefix}.name`} />
                                  ) : dealParticipantData.name
                                }
                              </TableCell>
                            </TableRow>
                          )
                        }
                        {
                          (isEditMode || isNewEntity || isNewDeal || isModalEditMode
                            || dealParticipantData.accessLevel) && (
                            <CheckAccess permissions={dealParticipantAccessLevelViewPermissions}>
                              <TableRow>
                                <TableCell>
                                  Access Level
                                  {
                                    isAccessLevelView && (
                                      <RequiredMark />
                                    )
                                  }
                                </TableCell>
                                <TableCell>
                                  {
                                    isEditMode || isNewEntity || isNewDeal || isModalEditMode ? (
                                      <FormFieldSelect
                                        isBordered
                                        withBlankElement
                                        isDisabled={(!isContactLinkedToUser && !isNewUserFormActive)
                                          || (accessLevelList && !accessLevelList.length)}
                                        name={`${formPrefix}.accessLevel`}
                                        optionsTotal={accessLevelList && accessLevelList.length}
                                      >
                                        {menuItemAccessLevelList}
                                      </FormFieldSelect>
                                    ) : dealParticipantData.accessLevel && dealParticipantData.accessLevel.title
                                  }
                                </TableCell>
                              </TableRow>
                            </CheckAccess>
                          )
                        }
                      </TableBody>
                    </Table>
                  </Card>
                </GridItem>
                {
                  !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode && (
                    <CheckAccess permissions={dealParticipantCommentListViewPermissions}>
                      <GridItem isFullWidth={isSmallScreen} isMediumWidth={!isSmallScreen}>
                        <Card isContainer withNoPadding>
                          <CommentTable
                            queryCommentId={queryCommentId}
                            commentList={commentList}
                            commentEditId={commentEditId}
                            commentActiveId={commentActiveId}
                            currentUser={currentUser}
                            contentHeight={contentHeight}
                            editPermissions={dealParticipantCommentEditPermissions}
                            deletePermissions={dealParticipantCommentDeletePermissions}
                            mentionPermission={CONTACT_VIEW}
                            hashtagPermission={SEARCH_RESULT_VIEW}
                            onSwitchCommentEditMode={switchCommentEditMode}
                            onDeleteCommentConfirm={onDeleteCommentConfirm}
                            onDeleteCommentCancel={onDeleteCommentCancel}
                            onCommentEntityLinkClick={onCommentEntityLinkClick}
                            onCommentActiveIdChange={onCommentActiveIdChange}
                            onCommentAttachmentFileViewClick={(
                              event: React.MouseEvent<Element, MouseEvent>, attachmentFile: FileItem,
                            ): void => {
                              onCommentAttachmentFileViewClick(
                                event,
                                attachmentFile,
                                (dealParticipantData.contact && dealParticipantData.contact.fullName) || null,
                              );
                            }}
                          />
                        </Card>
                      </GridItem>
                    </CheckAccess>
                  )
                }
              </Grid>
              {
                !isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode && (
                  <CheckAccess permissions={dealParticipantCommentCreatePermissions}>
                    <CommentForm
                      isCommentSaving={isCommentSaving}
                      isCommentFormActive={isCommentFormActive}
                      attachmentFiles={commonAttachmentFiles}
                      commentText={commentText}
                      mentionList={mentionList}
                      hashtagList={hashtagList}
                      commentAttachmentDropDeps={commentAttachmentDropDeps}
                      mentionRouteRoot={ROUTE_CONTACT}
                      hashtagRouteRoot={ROUTE_SEARCH}
                      onSaveComment={saveComment}
                      onCommentFieldChange={onCommentFieldChange}
                      onCommentFieldKeyDown={onCommentFieldKeyDown}
                      onCommentAttachmentFilesChange={onCommentAttachmentFilesChange}
                      onCommentAttachmentFileDelete={onCommentAttachmentFileDelete}
                      onCommentAttachmentFileError={onCommentAttachmentFileError}
                    />
                  </CheckAccess>
                )
              }
              {
                isModalEditMode ? (
                  <div>
                    {
                      isSaving && (
                        <Loader isInline isFloating isModal isSubmit />
                      )
                    }
                    <ControlButtonBlock
                      isModal
                      isModalEditMode={isModalEditMode}
                      isNewEntity={isNewEntity}
                      isSaveDisabled={isSaveDisabled}
                      withOffset={isRightDrawerOpen}
                      onCancelForm={onCancelForm}
                      onSubmitForm={onSubmitForm}
                    />
                  </div>
                ) : (
                  <ModalButtonBlock>
                    {
                      isNewEntity && (
                        <Button onClick={cancelChangesAndCloseModal}>
                          Cancel
                        </Button>
                      )
                    }
                    <Button
                      isDisabled={isSaving}
                      isActive={isNewEntity}
                      onClick={applyChangesAndCloseModal}
                    >
                      {isNewEntity ? 'Save' : 'Close'}
                    </Button>
                    {
                      isSaving && !isSmallScreen && (
                        <Loader isInline isModal isSmall />
                      )
                    }
                  </ModalButtonBlock>
                )
              }
            </Form>
            <LeaveConfirmModal
              isSubModal
              isOpen={isLeaveConfirmModalOpen}
              onConfirm={onLeaveConfirm}
              onCloseModal={closeLeaveConfirmModal}
            />
          </ModalLayout>
        )
      }
      <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default DealParticipantModal;
